import React, { useState, useEffect, useCallback } from 'react';
import { Cpu, HardDrive, Activity, Server, Container, Network, AlertCircle, RefreshCw, Settings, LogOut, X } from 'lucide-react';

export default function Dashboard() {
  const [time, setTime] = useState(new Date());
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [serverUrl, setServerUrl] = useState(localStorage.getItem('serverUrl') || 'https://api.carpscatalog.com');
  const [showSettings, setShowSettings] = useState(false);
  const [refreshInterval, setRefreshInterval] = useState(parseInt(localStorage.getItem('refreshInterval')) || 5000);
  const [isLoggedIn, setIsLoggedIn] = useState(localStorage.getItem('isLoggedIn') === 'true');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [loginError, setLoginError] = useState('');

  const handleLogin = (e) => {
    e.preventDefault();
    if (username === 'charles' && password === 'Charlie2005!') {
      localStorage.setItem('isLoggedIn', 'true');
      setIsLoggedIn(true);
      setLoginError('');
    } else {
      setLoginError('Invalid username or password');
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('isLoggedIn');
    setIsLoggedIn(false);
    setUsername('');
    setPassword('');
  };

  useEffect(() => {
    const timer = setInterval(() => setTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  const fetchStats = useCallback(async () => {
    try {
      const response = await fetch(`${serverUrl}/api/stats`);
      if (!response.ok) throw new Error('Failed to fetch stats');
      const data = await response.json();
      setStats(data);
      setError(null);
      setLoading(false);
    } catch (err) {
      setError(err.message);
      setLoading(false);
    }
  }, [serverUrl]);

  useEffect(() => {
    fetchStats();
    const interval = setInterval(fetchStats, refreshInterval);
    return () => clearInterval(interval);
  }, [fetchStats, refreshInterval]);

  const formatBytes = (bytes) => {
    if (!bytes) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
  };

  const formatUptime = (seconds) => {
    const days = Math.floor(seconds / 86400);
    const hours = Math.floor((seconds % 86400) / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    return `${days}d ${hours}h ${mins}m`;
  };

  const saveSettings = () => {
    localStorage.setItem('serverUrl', serverUrl);
    localStorage.setItem('refreshInterval', refreshInterval);
    setShowSettings(false);
    fetchStats();
  };

  const getUsageColor = (percent) => {
    if (percent < 60) return 'from-green-500 to-green-400';
    if (percent < 80) return 'from-yellow-500 to-yellow-400';
    return 'from-red-500 to-red-400';
  };

  // LOGIN
  if (!isLoggedIn) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 text-white flex items-center justify-center p-4">
        <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-2xl p-6 w-full max-w-sm">
          <div className="text-center mb-6">
            <Server className="w-12 h-12 text-purple-400 mx-auto mb-3" />
            <h1 className="text-2xl font-bold">Server Dashboard</h1>
            <p className="text-gray-400 mt-1 text-sm">Please log in to continue</p>
          </div>

          {loginError && (
            <div className="bg-red-500/20 border border-red-500 rounded-lg p-3 mb-4 text-center text-sm">
              {loginError}
            </div>
          )}

          <div className="space-y-3">
            <div>
              <label className="block mb-1 text-sm font-semibold">Username</label>
              <input
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleLogin(e)}
                className="w-full bg-white/10 border border-white/20 rounded-xl py-3 px-4 focus:outline-none focus:ring-2 focus:ring-purple-500 text-base"
                placeholder="Enter username"
                autoCapitalize="none"
              />
            </div>
            <div>
              <label className="block mb-1 text-sm font-semibold">Password</label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleLogin(e)}
                className="w-full bg-white/10 border border-white/20 rounded-xl py-3 px-4 focus:outline-none focus:ring-2 focus:ring-purple-500 text-base"
                placeholder="Enter password"
              />
            </div>
            <button
              onClick={handleLogin}
              className="w-full bg-purple-600 hover:bg-purple-700 active:bg-purple-800 py-3 rounded-xl font-semibold transition text-base mt-2"
            >
              Log In
            </button>
          </div>
        </div>
      </div>
    );
  }

  // SETTINGS
  if (showSettings) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 text-white p-4">
        <div className="max-w-lg mx-auto">
          <div className="flex items-center justify-between mb-6">
            <h1 className="text-2xl font-bold">Settings</h1>
            <button onClick={() => setShowSettings(false)} className="p-2 hover:bg-white/10 rounded-xl transition">
              <X className="w-6 h-6" />
            </button>
          </div>
          <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-2xl p-5 space-y-5">
            <div>
              <label className="block mb-2 font-semibold">Server URL</label>
              <input
                type="text"
                value={serverUrl}
                onChange={(e) => setServerUrl(e.target.value)}
                placeholder="https://api.carpscatalog.com"
                className="w-full bg-white/10 border border-white/20 rounded-xl py-3 px-4 focus:outline-none focus:ring-2 focus:ring-purple-500 text-base"
                autoCapitalize="none"
                autoCorrect="off"
              />
            </div>
            <div>
              <label className="block mb-2 font-semibold">Refresh Interval</label>
              <select
                value={refreshInterval}
                onChange={(e) => setRefreshInterval(parseInt(e.target.value))}
                className="w-full bg-slate-800 border border-white/20 rounded-xl py-3 px-4 focus:outline-none focus:ring-2 focus:ring-purple-500 text-base"
              >
                <option value="1000">1 second</option>
                <option value="2000">2 seconds</option>
                <option value="5000">5 seconds</option>
                <option value="10000">10 seconds</option>
                <option value="30000">30 seconds</option>
                <option value="60000">1 minute</option>
              </select>
            </div>
            <div className="flex gap-3 pt-2">
              <button
                onClick={saveSettings}
                className="flex-1 bg-purple-600 hover:bg-purple-700 active:bg-purple-800 py-3 rounded-xl font-semibold transition"
              >
                Save & Connect
              </button>
              <button
                onClick={() => setShowSettings(false)}
                className="flex-1 bg-white/10 hover:bg-white/20 py-3 rounded-xl font-semibold transition"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // MAIN DASHBOARD
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 text-white">
      {/* Sticky Header */}
      <div className="sticky top-0 z-10 bg-slate-900/80 backdrop-blur-md border-b border-white/10 px-4 py-3">
        <div className="max-w-7xl mx-auto flex justify-between items-center">
          <div className="flex items-center gap-2">
            <Server className="w-5 h-5 text-purple-400 shrink-0" />
            <div>
              <h1 className="text-base font-bold leading-tight">Server Dashboard</h1>
              <p className="text-xs text-gray-400 leading-tight">{stats?.system.hostname || 'Connecting...'}</p>
            </div>
          </div>
          <div className="flex items-center gap-1">
            <div className="text-sm font-mono font-semibold text-purple-300 mr-2">
              {time.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
            </div>
            <button onClick={fetchStats} className="p-2 hover:bg-white/10 active:bg-white/20 rounded-xl transition" title="Refresh">
              <RefreshCw className="w-5 h-5" />
            </button>
            <button onClick={() => setShowSettings(true)} className="p-2 hover:bg-white/10 active:bg-white/20 rounded-xl transition" title="Settings">
              <Settings className="w-5 h-5" />
            </button>
            <button onClick={handleLogout} className="p-2 hover:bg-red-500/20 active:bg-red-500/30 rounded-xl transition text-red-400" title="Logout">
              <LogOut className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto p-4 pb-8 space-y-4">

        {/* Error State */}
        {error && (
          <div className="bg-red-500/20 border border-red-500 rounded-2xl p-4 flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-red-400 shrink-0 mt-0.5" />
            <div>
              <div className="font-semibold text-sm">Connection Error</div>
              <div className="text-sm text-gray-300">{error}</div>
              <button onClick={() => setShowSettings(true)} className="text-sm text-purple-400 hover:text-purple-300 mt-1">
                Check settings →
              </button>
            </div>
          </div>
        )}

        {/* Loading State */}
        {loading && !stats && (
          <div className="text-center py-16">
            <RefreshCw className="w-10 h-10 animate-spin mx-auto mb-3 text-purple-400" />
            <p className="text-gray-400">Connecting to server...</p>
          </div>
        )}

        {stats && (
          <>
            {/* System Info — 2 cols on mobile, 4 on desktop */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {[
                { label: 'Platform', value: stats.system.platform },
                { label: 'Kernel', value: stats.system.kernel },
                { label: 'Uptime', value: formatUptime(stats.system.uptime) },
                { label: 'Architecture', value: stats.system.arch },
              ].map(({ label, value }) => (
                <div key={label} className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-2xl p-3">
                  <div className="text-xs text-gray-400 mb-1">{label}</div>
                  <div className="font-semibold text-sm leading-tight">{value}</div>
                </div>
              ))}
            </div>

            {/* CPU & Memory — stacked on mobile, side by side on desktop */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">

              {/* CPU */}
              <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-2xl p-5">
                <div className="flex items-center gap-2 mb-3">
                  <Cpu className="w-5 h-5 text-blue-400" />
                  <h3 className="font-semibold">CPU</h3>
                </div>
                <div className="text-4xl font-bold mb-2">{stats.cpu.usage.toFixed(1)}%</div>
                <div className="h-3 bg-white/10 rounded-full overflow-hidden mb-3">
                  <div
                    className={`h-full bg-gradient-to-r ${getUsageColor(stats.cpu.usage)} transition-all duration-500`}
                    style={{ width: `${stats.cpu.usage}%` }}
                  />
                </div>
                <div className="text-sm text-gray-300">
                  <div className="truncate">{stats.cpu.model}</div>
                  <div className="mt-0.5">{stats.cpu.cores} cores @ {stats.cpu.speed} GHz</div>
                </div>
                {stats.topProcesses?.cpu?.length > 0 && (
                  <div className="border-t border-white/10 pt-3 mt-3">
                    <div className="text-xs text-gray-400 mb-2">Top Processes</div>
                    <div className="space-y-1.5">
                      {stats.topProcesses.cpu.slice(0, 3).map((proc, i) => (
                        <div key={i} className="flex justify-between text-xs">
                          <span className="truncate flex-1 text-gray-200">{proc.name}</span>
                          <span className="text-blue-400 ml-2 shrink-0">{proc.cpu.toFixed(1)}%</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Memory */}
              <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-2xl p-5">
                <div className="flex items-center gap-2 mb-3">
                  <Activity className="w-5 h-5 text-green-400" />
                  <h3 className="font-semibold">Memory</h3>
                </div>
                <div className="text-4xl font-bold mb-2">{stats.memory.percentUsed.toFixed(1)}%</div>
                <div className="h-3 bg-white/10 rounded-full overflow-hidden mb-3">
                  <div
                    className={`h-full bg-gradient-to-r ${getUsageColor(stats.memory.percentUsed)} transition-all duration-500`}
                    style={{ width: `${stats.memory.percentUsed}%` }}
                  />
                </div>
                <div className="text-sm text-gray-300">
                  <div>{formatBytes(stats.memory.used)} / {formatBytes(stats.memory.total)}</div>
                  <div className="mt-0.5">{formatBytes(stats.memory.available)} available</div>
                </div>
                {stats.topProcesses?.memory?.length > 0 && (
                  <div className="border-t border-white/10 pt-3 mt-3">
                    <div className="text-xs text-gray-400 mb-2">Top Processes</div>
                    <div className="space-y-1.5">
                      {stats.topProcesses.memory.slice(0, 3).map((proc, i) => (
                        <div key={i} className="flex justify-between text-xs">
                          <span className="truncate flex-1 text-gray-200">{proc.name}</span>
                          <span className="text-green-400 ml-2 shrink-0">{proc.mem.toFixed(1)}%</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Storage */}
            <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-2xl p-5">
              <div className="flex items-center gap-2 mb-4">
                <HardDrive className="w-5 h-5 text-purple-400" />
                <h3 className="font-semibold">Storage</h3>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {stats.disks?.map((disk, index) => {
                  const customName = disk.mount === '/' ? 'Internal SSD' :
                    disk.mount === '/mnt/media' ? 'External HDD' :
                    disk.mount;
                  return (
                    <div key={index} className="bg-white/5 rounded-xl p-4">
                      <div className="flex justify-between items-start mb-2">
                        <div>
                          <div className="font-semibold text-sm">{customName}</div>
                          <div className="text-xs text-gray-400">{disk.name} • {disk.type}</div>
                        </div>
                        <div className="text-lg font-bold">{disk.percentUsed.toFixed(1)}%</div>
                      </div>
                      <div className="h-2 bg-white/10 rounded-full overflow-hidden mb-2">
                        <div
                          className={`h-full bg-gradient-to-r ${getUsageColor(disk.percentUsed)} transition-all duration-500`}
                          style={{ width: `${disk.percentUsed}%` }}
                        />
                      </div>
                      <div className="text-xs text-gray-400">
                        {formatBytes(disk.used)} / {formatBytes(disk.total)} • {formatBytes(disk.available)} free
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Docker Containers */}
            <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-2xl p-5">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <Container className="w-5 h-5 text-cyan-400" />
                  <h3 className="font-semibold">Docker</h3>
                </div>
                <div className="text-sm bg-white/10 px-3 py-1 rounded-full">
                  <span className="text-green-400 font-semibold">{stats.docker.running}</span>
                  <span className="text-gray-400"> / {stats.docker.total} running</span>
                </div>
              </div>

              {stats.docker.error && (
                <div className="text-sm text-yellow-400 mb-3">Docker not available: {stats.docker.error}</div>
              )}

              <div className="space-y-2">
                {stats.docker.containers.length === 0 ? (
                  <div className="text-gray-400 text-center py-4 text-sm">No containers found</div>
                ) : (
                  stats.docker.containers.map(container => {
                    const manualPorts = { 'qbittorrent': '8090' };
                    const customUrls = { 'threadfin': 'http://100.117.59.5:34400/web/' };

                    let containerUrl = customUrls[container.name.toLowerCase()];
                    if (!containerUrl) {
                      const portMatch = container.ports ? container.ports.match(/(\d+):/) : null;
                      const publicPort = portMatch ? portMatch[1] : manualPorts[container.name.toLowerCase()];
                      containerUrl = publicPort ? `http://100.117.59.5:${publicPort}` : null;
                    }

                    return (
                      <div key={container.id} className="bg-white/5 rounded-xl p-3 active:bg-white/10 transition">
                        <div className="flex items-center justify-between gap-2">
                          <div className="flex items-center gap-2 min-w-0">
                            <div className={`w-2 h-2 rounded-full shrink-0 ${container.state === 'running' ? 'bg-green-400' : 'bg-gray-500'}`} />
                            {containerUrl ? (
                              <a
                                href={containerUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="font-semibold text-sm hover:text-purple-400 transition truncate"
                              >
                                {container.name} ↗
                              </a>
                            ) : (
                              <span className="font-semibold text-sm truncate">{container.name}</span>
                            )}
                          </div>
                          <span className={`px-2 py-0.5 rounded-full text-xs font-semibold shrink-0 ${
                            container.state === 'running' ? 'bg-green-500/20 text-green-400' : 'bg-gray-500/20 text-gray-400'
                          }`}>
                            {container.state}
                          </span>
                        </div>
                        <div className="text-xs text-gray-400 mt-1 ml-4 truncate">{container.image}</div>
                      </div>
                    );
                  })
                )}
              </div>
            </div>

            {/* Network */}
            <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-2xl p-5">
              <div className="flex items-center gap-2 mb-4">
                <Network className="w-5 h-5 text-orange-400" />
                <h3 className="font-semibold">Network <span className="text-gray-400 font-normal text-sm">({stats.network.interface})</span></h3>
              </div>

              {/* IP Addresses — stack on mobile */}
              {stats.network.ipAddresses && (
                <div className="grid grid-cols-3 gap-2 mb-4 pb-4 border-b border-white/10">
                  {[
                    { label: 'Local IP', value: stats.network.ipAddresses.local },
                    { label: 'WARP IP', value: stats.network.ipAddresses.tailscale },
                    { label: 'Public IP', value: stats.network.ipAddresses.public },
                  ].map(({ label, value }) => (
                    <div key={label}>
                      <div className="text-xs text-gray-400 mb-0.5">{label}</div>
                      <div className="text-xs font-semibold break-all">{value || 'N/A'}</div>
                    </div>
                  ))}
                </div>
              )}

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <div className="text-xs text-gray-400 mb-1">Download</div>
                  <div className="text-2xl font-bold text-green-400">{formatBytes(stats.network.rxSec)}/s</div>
                  <div className="text-xs text-gray-400 mt-1">Total: {formatBytes(stats.network.rx)}</div>
                </div>
                <div>
                  <div className="text-xs text-gray-400 mb-1">Upload</div>
                  <div className="text-2xl font-bold text-blue-400">{formatBytes(stats.network.txSec)}/s</div>
                  <div className="text-xs text-gray-400 mt-1">Total: {formatBytes(stats.network.tx)}</div>
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="text-center text-gray-500 text-xs pt-2">
              Last updated: {new Date(stats.timestamp).toLocaleTimeString()} • Refreshing every {refreshInterval / 1000}s
            </div>
          </>
        )}
      </div>
    </div>
  );
}
